// @flow
import { consts as uploaderConsts } from "uploader";
import { getLogger } from "common/logger";
import createReducer from "common/reducerBase";
import { isMobile, isSafari } from "utils/domUtils";
import { isSupported as isCameraMediaSupported } from "../../utils/camera";
import { types } from "../actions";
import WidgetConfigRecord, { takeUiParams } from "../data/WidgetConfigRecord";

type ConfigParams = {
	cloudName: string,
	secure: boolean,
	inlineMode: boolean,
	sources: string[],
	defaultSource: ?string,
	multiple: ?boolean,
	googleApiKey: ?string,
	googleDriveClientId: ?string,
	dropboxAppKey: ?string,
	facebookAppId: ?string,
	instagramServer: ?string,
	shutterstockServer: ?string,
	searchBySites: ?string[],
	searchByRights: ?boolean,
	autoMinimize: ?boolean,
	useTagsCallback: ?boolean,
	useUploadPresetsCallback: ?boolean,
	text: ?Object,
	language: ?string,
	maxFiles?: number,
	showAdvancedOptions: ?boolean,
	showCompletedButton: ?boolean,
	tags?: string[],
	publicId?: string,
	uploadPreset?: string,
	showPoweredBy?: boolean,
	croppingShowBackButton?: boolean,
	croppingShowDimensions?: boolean,
	croppingAspectRatio?: boolean,
	croppingCoordinatesMode?: boolean,
	theme?: string,
	istockServer?: string,
};

const logger = getLogger();

const SRCS = uploaderConsts.SOURCES;

const isSecureValidator = (config, secure) => ({
	valid: !!secure,
	reason: "'secure' option must be turned on",
});

const isNotSafariValidator = () => ({
	valid: !isSafari(),
	reason: "source is not supported on the Safari browser",
});

const isCameraAvailable = (config, secure) => {
	const loc = self.location;
	const validations = [
		isSecureValidator(config, secure),
		{
			valid: (loc.hostname === "localhost" || loc.protocol === "https:")
				&& isCameraMediaSupported() && !isMobile(),
			reason: "only supported on https and desktop browsers",
		},
	];

	const firstInvalid = validations.find((v) => !v.valid);

	return firstInvalid || { valid: true };
};

const isGDriveAvailable = (config, secure) => {
	const isSecure = isSecureValidator(config, secure);
	return isSecure.valid ? isNotSafariValidator() : isSecure;
};


const sourcesValidators = {
	[SRCS.INSTAGRAM]: isSecureValidator,
	[SRCS.DROPBOX]: isSecureValidator,
	[SRCS.GOOGLE_DRIVE]: isGDriveAvailable,
	[SRCS.IMAGE_SEARCH]: (config) => ({
		valid: !!config.googleApiKey,
		reason: "Google API Key must be provided",
	}),
	[SRCS.CAMERA]: isCameraAvailable,
	[SRCS.SHUTTERSTOCK]: isSecureValidator,
	[SRCS.ISTOCK]: isSecureValidator,
	[SRCS.UNSPLASH]: isSecureValidator,
};

/**
 * remove duplicates from sources config and validate each source
 * @param config
 * @param secure
 * @returns {Array<sources>} validated sources list
 */
const getValidSources = (config, secure) => {
	const validSources = [].concat(// using concat here to make sure we always use a plain array and not an immutable object
		config.sources
		? [...new Set(config.sources)]
			.filter((source) => {
				const result = !sourcesValidators[source] ? { valid: true }
					: sourcesValidators[source](config, secure);

				if (!result.valid) {
					logger.warn(`[widgetConfigReducer]: removing ${source} - ${result.reason}`);
				}

				return result.valid;
			}) : [],
	);

	if (!validSources.length) {
		logger.warn("[widgetConfigReducer]: no valid sources configured, defaulting to local");
		validSources.push(SRCS.LOCAL);
	}

	return validSources;
};

const getValidDefaultSource = (defaultSrc, sources) => {
	if (!defaultSrc || !~sources.indexOf(defaultSrc)) {
		logger.log(`[widgetConfigReducer]: default source ${defaultSrc || "null"} is not present in valid&enabled sources`);
		defaultSrc = sources[0]; // use the first one as the default
	}

	return defaultSrc;
};

const getIsShowAdvancedOptions = (payload: ConfigParams) => payload.showAdvancedOptions
	&& !payload.tags && !payload.publicId; // cant have advanced options with either tags or public id provided

const getValidConfig = (state, payload: ConfigParams) => {
	const uiConfig = takeUiParams(payload);
	const validSources = uiConfig.sources && uiConfig.sources && getValidSources(uiConfig, payload.secure);
	const searchBySites = uiConfig.searchBySites && uiConfig.searchBySites.length
		? uiConfig.searchBySites : ["all"];

	return {
		...uiConfig,
		showAdvancedOptions: getIsShowAdvancedOptions(payload),
		searchBySites,
		sources: (validSources || state.sources),
		defaultSource: validSources
			? getValidDefaultSource(payload.defaultSource, validSources) : state.defaultSource,
	};
};

export default createReducer(WidgetConfigRecord(), {
	[types.SET_WIDGET_CONFIG]: (state: Object, { payload }: { payload: ConfigParams }) => state.merge(getValidConfig(state, payload)),
});
